import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  apiErrorMessage,
  applyLocale,
  currentLocale,
  i18n,
  LANGUAGE_OPTIONS,
  toSupportedLocale,
} from '../../i18n';
import { useUpdateMyProfile } from '../../lib/profile/useUpdateMyProfile';
import { SettingsRow } from './SettingsRow';
import { SettingsSelect } from './SettingsSelect';

type ProfileLanguageRowProps = {
  onSaved: (message: string) => void;
};

/** Each name is tagged with its own language so screen readers pronounce it correctly. */
const OPTIONS = LANGUAGE_OPTIONS.map((option) => ({ ...option, lang: option.value }));

/** Switches instantly on this device, then saves to the account so every device follows. */
export function ProfileLanguageRow({ onSaved }: ProfileLanguageRowProps) {
  const { t } = useTranslation('settings');
  const updateProfile = useUpdateMyProfile();
  const [switching, setSwitching] = useState(false);
  const [switchFailed, setSwitchFailed] = useState(false);
  const current = currentLocale();

  const change = async (value: string) => {
    const locale = toSupportedLocale(value);
    if (!locale || locale === current) return;
    setSwitching(true);
    setSwitchFailed(false);
    updateProfile.reset();
    try {
      await applyLocale(locale);
    } catch {
      setSwitchFailed(true);
      return;
    } finally {
      setSwitching(false);
    }
    // Read from the instance, not `t`: this render's `t` still speaks the old language.
    updateProfile.mutate(
      { locale },
      { onSuccess: () => onSaved(i18n.t('settings:profile.language.saved')) },
    );
  };

  const error = switchFailed
    ? t('profile.language.switchError')
    : updateProfile.isError
      ? apiErrorMessage(updateProfile.error, t('profile.language.saveError'))
      : null;

  return (
    <SettingsRow
      title={t('profile.language.title')}
      description={t('profile.language.description')}
      last
    >
      <div className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto">
        <SettingsSelect
          ariaLabel={t('profile.language.label')}
          value={current}
          options={OPTIONS}
          disabled={switching || updateProfile.isPending}
          onChange={(value) => void change(value)}
        />
        {error ? (
          <p role="alert" className="m-0 text-[13px]" style={{ color: 'var(--gs-bad)' }}>
            {error}
          </p>
        ) : null}
      </div>
    </SettingsRow>
  );
}
